/* eslint-disable @angular-eslint/component-selector */
import { Component, inject, OnInit } from '@angular/core';
import { StatusBar } from '@capacitor/status-bar';
import { DataCentralService } from '@shared/providers/core';

import { DeviceDetectionService } from '@shared/providers/device-detection.service';
import { Router } from '@angular/router';
import { VersionCheckerService } from '@shared/modals/version-checker/version-checker.service';
import { Capacitor } from '@capacitor/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { environment } from 'environments/environment';
import LogRocket from 'logrocket';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  /**DEPENDENCIES */
  private readonly _dataCentralService = inject(DataCentralService);
  private readonly _deviceService = inject(DeviceDetectionService);
  private readonly _router = inject(Router);
  private readonly versionCheckerService = inject(VersionCheckerService);
  /**PUBLIC VARIABLES */

  constructor() {
    this.setupDeviceRedirection();
  }

  public ngOnInit(): void {
    this.initApp();
  }
  public async initApp(): Promise<void> {
    // Inicializar la base de datos local
    await this._dataCentralService.initLocalDB();
    // Verificando si estamos en un dispositivo nativo
    if (Capacitor.isNativePlatform()) {
      this.applyStyles();
      this.initSafeArea();
      this.versionCheckerService.checkVersion();
    }
    if (environment.production) {
      LogRocket.init('policia/psp');
      const user = this._dataCentralService.user;
      if(!user) return;
      LogRocket.identify('' + user.Username, {
        name: user.FirstName + ' ' + user.LastName,
        email: user.Email,
      });
    }
  }

  /**PUBLIC METHODS */
  /**PRIVATE METHODS */
  private async applyStyles() {
    try {
      await StatusBar.setBackgroundColor({
        color: '#07152c'
      });
    } catch (error) {
      console.warn('No se pudo aplicar los estilos de status bar, estas en nativo?');
      console.warn(error);
    }
  }

  private async initSafeArea() {
    try {
      if (Capacitor.getPlatform() !== 'android') return;

      const globalStyles = document.documentElement?.style;
      if (!globalStyles) {
        console.warn('Unable to access global styles.');
        return;
      }

      const safeAreaResult = await SafeArea.getSafeAreaInsets();
      const insets = safeAreaResult?.insets || { top: 0, bottom: 0 };

      const innerHeight = window.innerHeight || 0;
      const screenHeight = screen.height || 0;
      const screenY = window.screenY || 0;

      const heightDifference = screenHeight - innerHeight;
      const diffThreshold = 8;

      const topInset = insets.top || 0;
      const bottomInset = insets.bottom || 0;

      const topOverlap = Math.abs(topInset - screenY) > diffThreshold;
      const shouldApplyTop = topOverlap && topInset > 0;

      if (shouldApplyTop) {
        globalStyles.setProperty('--ion-safe-area-top', `${topInset}px`);
      }

      const bottomUsed = Math.abs(heightDifference - bottomInset) > diffThreshold;
      const shouldApplyBottom = bottomUsed && bottomInset > 0;

      if (shouldApplyBottom) {
        globalStyles.setProperty('--ion-safe-area-bottom', `${bottomInset}px`);
      }
    } catch (err) {
      console.error('Error initializing safe area:', err);
    }
  }

  /**
   * Web only: hop back to /tabs when the viewport shrinks below the desktop
   * breakpoint. The /tabs → /desktop direction is owned by DeviceRedirectGuard.
   */
  private setupDeviceRedirection(): void {
    if (Capacitor.isNativePlatform()) return;

    this._deviceService.isDesktop$.subscribe((isDesktop) => {
      if (!isDesktop && this._router.url.startsWith('/desktop')) {
        this._router.navigate(['/tabs']);
      }
    });
  }
}
